<template>
  <!-- The AI section of the Jobs settings. A showcase more than a form: an animated banner across the full width of the panel, the AI
       switch, and under it two sections that the switch unlocks: AI Models (five cards to pick from) and Resume Prompt. Everything that moves stops for people who prefer less motion. -->
  <section aria-labelledby="ai-heading" class="ai">
    <!-- Hero -->
    <div class="ai-hero relative overflow-hidden text-white">
      <span class="ai-blob ai-blob-a" aria-hidden="true"></span>
      <span class="ai-blob ai-blob-b" aria-hidden="true"></span>
      <span class="ai-blob ai-blob-c" aria-hidden="true"></span>
      <span v-for="star in STARS" :key="star.id" class="ai-star" :style="star.style" aria-hidden="true"></span>

      <div class="relative px-6 py-[18px] flex items-center gap-4">
        <div class="ai-orb flex-shrink-0" aria-hidden="true">
          <span class="ai-orb-ring"></span>
          <span class="ai-orb-core">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="1.7" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" :d="SPARKLES"/></svg>
          </span>
        </div>
        <div class="min-w-0">
          <div class="flex items-center gap-2">
            <h3 id="ai-heading" class="ai-title text-xl sm:text-2xl font-bold leading-7 tracking-tight">Timelydo AI</h3>
            <span class="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-white/15 text-white/80 leading-none">New</span>
          </div>
          <p class="text-[13px] text-white/75 leading-snug">Your job search with an assistant that knows your profile.</p>
        </div>
      </div>

      <!-- A line that keeps changing, like an assistant thinking out loud -->
      <div class="relative border-t border-white/10 bg-black/20 px-6 h-9 flex items-center text-[13px] text-white/80" aria-hidden="true">
        <span class="w-1.5 h-1.5 rounded-full bg-emerald-400 mr-2.5 ai-live"></span>
        <span class="ai-ticker relative flex-1 h-5 overflow-hidden">
          <span v-for="(line, index) in TICKER" :key="line" class="ai-ticker-line" :style="{ animationDelay: `${index * 3}s` }">{{ line }}</span>
        </span>
      </div>
    </div>

    <!-- The rest sits in the same column as the other sections -->
    <div class="px-6 pb-4 max-w-2xl">
    <!-- Checking OpenAI -->
    <div v-if="checking" class="mt-5 space-y-4 animate-pulse" aria-hidden="true">
      <div class="h-16 rounded-lg bg-gray-100 dark:bg-gray-800"></div>
      <div class="h-px bg-gray-200 dark:bg-gray-700"></div>
      <div class="h-24 rounded-lg bg-gray-100 dark:bg-gray-800"></div>
    </div>

    <!-- OpenAI is not connected: say what to do first, and what it unlocks -->
    <template v-else-if="!ready">
      <div class="ai-rise py-5" role="status">
        <p class="text-base font-semibold text-gray-900 dark:text-white">{{ openai.stale ? 'Your OpenAI key stopped working' : 'Turn on the OpenAI integration first' }}</p>
        <p class="text-sm mt-1.5 text-gray-600 dark:text-gray-300 leading-relaxed">
          {{ openai.stale ? 'OpenAI no longer accepts the key you saved. Replace it to use AI here.' : 'AI here runs on your own OpenAI account, so the OpenAI integration has to be on before you can use it.' }}
          Once it is on, you can get help from AI to generate resumes automatically and to match jobs to your profile.
        </p>
        <p class="mt-2 flex items-start gap-1.5 text-xs leading-snug text-emerald-700 dark:text-emerald-400">
          <svg class="w-3.5 h-3.5 flex-shrink-0 mt-px" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/></svg>
          <span>No tokens are used, input or output, until you ask for a resume to be generated. Resumes are never made automatically.</span>
        </p>

        <ol class="mt-3.5 space-y-2">
          <li v-for="(step, index) in STEPS" :key="step" class="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-200">
            <span class="w-5 h-5 rounded-full bg-gradient-to-br from-blue-500 to-violet-500 text-white text-[11px] font-bold inline-flex items-center justify-center flex-shrink-0">{{ index + 1 }}</span>
            {{ step }}
          </li>
        </ol>

        <RouterLink to="/integrations/openai" data-turbo-prefetch="false" @click="$emit('close')" class="ai-cta mt-4 inline-flex items-center gap-2 h-10 px-5 rounded-xl text-sm font-semibold text-white">
          {{ openai.stale ? 'Fix the OpenAI key' : 'Set up OpenAI' }}
          <svg class="w-4 h-4 ai-arrow" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"/></svg>
        </RouterLink>

        <ul class="mt-4 flex flex-wrap gap-2 opacity-80" aria-label="What AI will do for you">
          <li v-for="feature in FEATURES" :key="feature.title" :title="feature.text" class="inline-flex items-center gap-1.5 h-7 pl-2 pr-2.5 rounded-full border border-gray-200 dark:border-gray-700 text-xs font-medium text-gray-700 dark:text-gray-200">
            <svg class="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" stroke-width="1.7" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" :d="feature.icon"/></svg>
            {{ feature.title }}
          </li>
        </ul>
      </div>

    </template>

    <!-- OpenAI is connected: the AI switch first, then the two sections it unlocks. They are always there, and dimmed with a message until AI is on. -->
    <template v-else>
      <!-- AI on or off -->
      <div class="ai-rise py-4">
        <div class="flex items-center gap-4">
          <div class="min-w-0 flex-1">
            <h4 class="text-sm font-semibold text-gray-900 dark:text-white">{{ store.settings.ai_enabled ? 'AI is on' : 'Turn on AI' }}</h4>
            <p class="text-xs mt-0.5 text-gray-500 dark:text-gray-400 leading-relaxed">Get help with resumes and match jobs to your profile. It runs on your OpenAI account.</p>
            <p class="mt-1.5 flex items-start gap-1.5 text-[11px] leading-snug text-emerald-700 dark:text-emerald-400">
              <svg class="w-3.5 h-3.5 flex-shrink-0 mt-px" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/></svg>
              <span>No tokens are used, input or output, until you ask for a resume to be generated. Resumes are never made automatically, and these settings cost nothing.</span>
            </p>
          </div>
          <button type="button" role="switch" :aria-checked="store.settings.ai_enabled" :disabled="saving" @click="toggle" aria-label="Enable AI"
            class="relative w-14 h-8 rounded-full flex-shrink-0 transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500 disabled:opacity-60"
            :class="store.settings.ai_enabled ? 'ai-on' : 'bg-gray-300 dark:bg-gray-600'">
            <span class="absolute top-1 left-1 w-6 h-6 rounded-full bg-white shadow-md inline-flex items-center justify-center transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]"
              :class="store.settings.ai_enabled ? 'translate-x-6 text-violet-600' : 'text-gray-400'">
              <svg class="w-3.5 h-3.5" :class="store.settings.ai_enabled ? 'ai-spark' : ''" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path :d="SPARK_FILLED"/></svg>
            </span>
          </button>
        </div>
      </div>

      <hr class="border-gray-200 dark:border-gray-700">

      <!-- AI Models and Resume Prompt -->
      <div class="relative pt-1">
        <div role="tablist" aria-label="AI settings" class="flex gap-1 border-b border-gray-200 dark:border-gray-700">
          <button v-for="item in TABS" :key="item.id" type="button" role="tab" :id="`ai-tab-${item.id}`" :aria-selected="tab === item.id" :aria-controls="`ai-panel-${item.id}`"
            @click="tab = item.id"
            class="relative h-10 px-3 -mb-px text-[13px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50 rounded-t-lg"
            :class="tab === item.id ? 'text-violet-700 dark:text-violet-300' : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'">
            {{ item.label }}
            <span class="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-gradient-to-r from-blue-500 via-violet-500 to-fuchsia-500 transition-transform duration-300 origin-center" :class="tab === item.id ? 'scale-x-100' : 'scale-x-0'"></span>
          </button>
        </div>

        <!-- The page picked. Without AI the tabs still work, so both can be looked at, but the page is out of reach under a frosted light blue layer. -->
        <div class="relative" :class="enabled ? '' : 'min-h-[13rem]'">
        <div :class="enabled ? '' : 'select-none'" :inert="!enabled" :aria-hidden="!enabled">
          <!-- AI Models: the model, five cards, one picked -->
          <div v-if="tab === 'models'" id="ai-panel-models" role="tabpanel" aria-labelledby="ai-tab-models">
        <div class="py-4">
          <h4 id="ai-model-label" class="text-sm font-semibold text-gray-900 dark:text-white">Model</h4>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5 mb-2.5">
            The OpenAI model that does the work. GPT-5 Mini is the default, a good balance of quality, speed and cost.
            <HelpLink path="job-tracker/turn-on-ai#cost" label="How prices work" class="ml-1" />
          </p>

          <div v-if="ai.loading && !ai.loaded" class="grid grid-cols-2 sm:grid-cols-3 gap-2 animate-pulse" aria-hidden="true">
            <div v-for="n in 5" :key="n" class="h-14 rounded-xl bg-gray-100 dark:bg-gray-800"></div>
          </div>
          <div v-else ref="cards" role="radiogroup" aria-labelledby="ai-model-label" :aria-disabled="!store.settings.ai_enabled" class="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            <button v-for="(model, index) in modelOptions" :key="model.id" type="button" role="radio" :aria-checked="store.settings.ai_model === model.id"
              :tabindex="store.settings.ai_model === model.id || (index === 0 && !modelOptions.some((m) => m.id === store.settings.ai_model)) ? 0 : -1"
              :disabled="!store.settings.ai_enabled" @click="choose(model.id)" @keydown="onModelKey($event, index)" @pointermove="spotlight"
              class="ai-card ai-rise group relative text-left rounded-xl focus:outline-none" :style="{ animationDelay: `${index * 50}ms` }">
              <span class="ai-card-face relative block overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] px-3 py-2.5 pr-9">
                <span class="flex items-center gap-1.5">
                  <span class="text-[13px] font-semibold text-gray-900 dark:text-white whitespace-nowrap">{{ model.label }}</span>
                  <span v-if="model.default" class="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 leading-none whitespace-nowrap">Default</span>
                </span>
                <span class="block text-[11px] mt-0.5 text-gray-500 dark:text-gray-400 truncate">{{ NOTES[model.id] || 'Chosen earlier' }}</span>
                <!-- What it costs per million tokens, and a bar for what a resume costs next to the other models -->
                <span class="block text-[10.5px] mt-1.5 tabular-nums text-gray-600 dark:text-gray-300 whitespace-nowrap" :title="priceTitle(model)">
                  <template v-if="model.pricing">{{ formatPrice(model.pricing.input) }} in · {{ formatPrice(model.pricing.output) }} out</template>
                  <template v-else>Price not listed</template>
                </span>
                <span class="block h-1 mt-1 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden" aria-hidden="true">
                  <span class="block h-full rounded-full bg-gradient-to-r from-blue-500 to-violet-500 transition-all duration-500" :style="{ width: `${barWidth(model)}%` }"></span>
                </span>
              </span>
              <span class="ai-check absolute right-2.5 top-2.5 w-4 h-4 rounded-full bg-gradient-to-br from-blue-500 to-violet-500 text-white inline-flex items-center justify-center transition-all duration-300"
                :class="store.settings.ai_model === model.id ? 'opacity-100 scale-100' : 'opacity-0 scale-50'" aria-hidden="true">
                <svg class="w-2.5 h-2.5" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/></svg>
              </span>
            </button>
          </div>

          <!-- The chosen model's prices in full, and every model side by side -->
          <section v-if="chosen && ai.loaded" class="mt-3.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/70 dark:bg-[#161b22] p-3.5" aria-live="polite">
            <div class="flex items-center justify-between gap-2">
              <h5 class="text-[13px] font-semibold text-gray-900 dark:text-white">{{ chosen.label }} pricing</h5>
              <span class="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400">per 1 million tokens</span>
            </div>

            <div v-if="chosen.pricing" class="grid grid-cols-3 gap-2 mt-2.5">
              <div v-for="tile in priceTiles(chosen.pricing)" :key="tile.label" class="rounded-lg bg-white dark:bg-[#1C2128] border border-gray-100 dark:border-gray-700 px-2.5 py-2" :title="tile.hint">
                <span class="block text-[10.5px] text-gray-500 dark:text-gray-400">{{ tile.label }}</span>
                <span class="block text-sm font-semibold tabular-nums text-gray-900 dark:text-white">{{ tile.value }}</span>
              </div>
            </div>
            <p v-else class="text-xs text-gray-500 dark:text-gray-400 mt-2">There is no price listed for this model, so its cost cannot be shown.</p>

            <p v-if="chosen.pricing" class="text-xs text-gray-600 dark:text-gray-300 mt-2.5">
              A typical resume costs about <strong class="font-semibold text-gray-900 dark:text-white tabular-nums">{{ formatCost(typicalResumeCost(chosen.pricing)) }}</strong>
              <span class="text-gray-400 dark:text-gray-500"> ({{ number(TYPICAL_RESUME.input) }} tokens in, {{ number(TYPICAL_RESUME.output) }} out)</span>.
              <template v-if="cheapest && cheapest.id !== chosen.id">The cheapest is {{ cheapest.label }}, at about {{ formatCost(typicalResumeCost(cheapest.pricing)) }}.</template>
              <template v-else-if="cheapest">This is the cheapest model.</template>
            </p>

            <button type="button" @click="showCompare = !showCompare" :aria-expanded="showCompare"
              class="mt-2.5 inline-flex items-center gap-1 text-xs font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 focus:outline-none focus-visible:underline">
              {{ showCompare ? 'Hide the comparison' : 'Compare all models' }}
              <svg class="w-3 h-3 transition-transform" :class="showCompare ? 'rotate-180' : ''" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/></svg>
            </button>

            <div v-if="showCompare" class="mt-2 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128]">
              <table class="w-full text-left text-xs tabular-nums">
                <thead>
                  <tr class="text-[10.5px] uppercase tracking-wider text-gray-400 dark:text-gray-500">
                    <th scope="col" class="px-2.5 py-1.5 font-semibold">Model</th>
                    <th scope="col" class="px-2.5 py-1.5 font-semibold text-right">Input</th>
                    <th scope="col" class="px-2.5 py-1.5 font-semibold text-right">Cached</th>
                    <th scope="col" class="px-2.5 py-1.5 font-semibold text-right">Output</th>
                    <th scope="col" class="px-2.5 py-1.5 font-semibold text-right">Per resume</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
                  <tr v-for="model in modelOptions" :key="model.id" :class="model.id === chosen.id ? 'bg-blue-50/70 dark:bg-blue-900/20 font-semibold text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-300'">
                    <th scope="row" class="px-2.5 py-1.5 font-inherit whitespace-nowrap">{{ model.label }}</th>
                    <template v-if="model.pricing">
                      <td class="px-2.5 py-1.5 text-right">{{ formatPrice(model.pricing.input) }}</td>
                      <td class="px-2.5 py-1.5 text-right">{{ formatPrice(model.pricing.cached_input) }}</td>
                      <td class="px-2.5 py-1.5 text-right">{{ formatPrice(model.pricing.output) }}</td>
                      <td class="px-2.5 py-1.5 text-right">{{ formatCost(typicalResumeCost(model.pricing)) }}</td>
                    </template>
                    <td v-else colspan="4" class="px-2.5 py-1.5 text-right text-gray-400 dark:text-gray-500">Price not listed</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-2.5">
              Dollars per million tokens<template v-if="ai.pricesAsOf">, checked {{ ai.pricesAsOf }}</template>. Cached is what OpenAI charges for the start of a prompt it has seen a moment ago.
              A resume cost is an estimate. Your OpenAI invoice has the exact amount.
            </p>
          </section>

          <p v-if="ai.loaded && !ai.live" class="text-xs text-gray-400 dark:text-gray-500 mt-2.5">OpenAI could not be reached, so every model is listed, whether or not your key can use it. Open this section again to retry.</p>
          <p v-if="ai.error" class="text-xs text-red-500 dark:text-red-400 mt-2.5" role="alert">{{ ai.error }}</p>
        </div>
          </div>

          <!-- Resume Prompt: what goes into it, and the prompt itself -->
        <div v-else id="ai-panel-prompt" role="tabpanel" aria-labelledby="ai-tab-prompt" class="ai-rise py-3">
          <div class="flex items-end justify-between gap-3">
            <label for="ai-prompt" class="text-sm font-semibold text-gray-900 dark:text-white">Resume prompt sent to the AI <HelpLink path="job-tracker/turn-on-ai#prompt" label="About the prompt" class="ml-1.5 align-middle" /></label>
            <span class="text-[11px] tabular-nums" :class="draft.length > MAX_PROMPT ? 'text-red-500' : 'text-gray-400 dark:text-gray-500'">{{ draft.length }} / {{ MAX_PROMPT }}</span>
          </div>
          <textarea id="ai-prompt" v-model="draft" rows="10" spellcheck="true"
            class="mt-1.5 w-full h-56 resize-none px-3 py-2 text-[13px] leading-relaxed rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-violet-500/40 focus:border-violet-400"></textarea>

          <div class="mt-2.5 flex items-center gap-2">
            <button type="button" @click="savePrompt" :disabled="saving || !dirty || draft.length > MAX_PROMPT"
              class="ai-cta inline-flex items-center h-9 px-4 rounded-lg text-[13px] font-semibold text-white disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none disabled:animate-none">Save prompt</button>
            <button v-if="store.settings.ai_resume_prompt_custom || dirty" type="button" @click="resetPrompt" :disabled="saving"
              class="inline-flex items-center h-9 px-3 rounded-lg text-[13px] font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50">Reset to default</button>
            <span v-if="savedNote" class="ml-1 text-xs font-medium text-emerald-600 dark:text-emerald-400" role="status">{{ savedNote }}</span>
          </div>
        </div>
        </div>

        <!-- Without AI the page sits under a frosted light blue layer: what is under it still shows, softly, and the reason is written on top. No box. -->
        <div v-if="!enabled" class="ai-locked absolute inset-0 rounded-lg flex flex-col items-center justify-center px-6 text-center text-blue-950 dark:text-white" role="status">
          <svg class="w-6 h-6 text-blue-700 dark:text-white/80" fill="none" stroke="currentColor" stroke-width="1.7" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"/></svg>
          <p class="mt-3 text-base font-semibold">Enable AI to use these sections</p>
          <p class="mt-1 text-sm text-blue-900/70 dark:text-white/70 max-w-xs leading-relaxed">AI Models and Resume Prompt unlock as soon as AI is on.</p>
        </div>
        </div>
      </div>
    </template>

    <p v-if="error" class="mt-3 text-xs text-red-500 dark:text-red-400" role="alert">{{ error }}</p>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { useJobSearchStore } from 'stores/jobSearch.js'
import { useOpenAiStore } from 'stores/openai.js'
import { useJobAiStore } from 'stores/jobAi.js'
import HelpLink from 'controllers/vue_components/shared/HelpLink.vue'
import { TYPICAL_RESUME, formatCost, formatPrice, typicalResumeCost } from 'utils/cost.js'

defineEmits(['close'])

// Heroicons (outline): the sparkles, a document with text (resumes) and a magnifier on a target (matching).
const SPARKLES = 'M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z'
const SPARK_FILLED = 'M9 4.5a.75.75 0 0 1 .721.544l.813 2.846a3.75 3.75 0 0 0 2.576 2.576l2.846.813a.75.75 0 0 1 0 1.442l-2.846.813a3.75 3.75 0 0 0-2.576 2.576l-.813 2.846a.75.75 0 0 1-1.442 0l-.813-2.846a3.75 3.75 0 0 0-2.576-2.576l-2.846-.813a.75.75 0 0 1 0-1.442l2.846-.813A3.75 3.75 0 0 0 7.466 7.89l.813-2.846A.75.75 0 0 1 9 4.5ZM18 1.5a.75.75 0 0 1 .728.568l.258 1.036c.236.94.97 1.674 1.91 1.91l1.036.258a.75.75 0 0 1 0 1.456l-1.036.258c-.94.236-1.674.97-1.91 1.91l-.258 1.036a.75.75 0 0 1-1.456 0l-.258-1.036a2.625 2.625 0 0 0-1.91-1.91l-1.036-.258a.75.75 0 0 1 0-1.456l1.036-.258a2.625 2.625 0 0 0 1.91-1.91l.258-1.036A.75.75 0 0 1 18 1.5Z'

// What AI will be used for. These are not built yet: the section says so ("Coming soon") and only keeps the choices ready.
const FEATURES = [
  {
    title: 'Resumes written for you', text: 'Turn your profile into a resume in seconds, and tailor it to each job you apply to.',
    icon: 'M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z',
  },
  {
    title: 'Jobs matched to you', text: 'See how well each job fits your profile, so the best ones rise to the top.',
    icon: 'M15.75 15.75l-2.489-2.489m0 0a3.375 3.375 0 1 0-4.773-4.773 3.375 3.375 0 0 0 4.774 4.774ZM21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z',
  },
]
const STEPS = ['Turn on the OpenAI integration', 'Paste your OpenAI key and validate it', 'Come back here and switch AI on']
const TICKER = ['Writing your resume', 'Matching jobs to your profile', 'Tailoring every application', 'Finding what fits you best']

// The prices on the cards and in the panel under them.
const chosen = computed(() => modelOptions.value.find((model) => model.id === store.settings.ai_model) || null)
const costs = computed(() => modelOptions.value.map((model) => typicalResumeCost(model.pricing)).filter((cost) => cost !== null))
const cheapest = computed(() => {
  const priced = modelOptions.value.filter((model) => model.pricing)
  return priced.length ? priced.reduce((best, model) => (typicalResumeCost(model.pricing) < typicalResumeCost(best.pricing) ? model : best)) : null
})
// A bar for what a resume costs next to the dearest model (never empty, so the cheapest still shows).
const barWidth = (model) => {
  const cost = typicalResumeCost(model.pricing)
  const dearest = Math.max(0, ...costs.value)
  return cost === null || dearest === 0 ? 0 : Math.max(6, Math.round((cost / dearest) * 100))
}
const priceTitle = (model) => (model.pricing
  ? `Per 1 million tokens: ${formatPrice(model.pricing.input)} in, ${formatPrice(model.pricing.cached_input)} cached in, ${formatPrice(model.pricing.output)} out. About ${formatCost(typicalResumeCost(model.pricing))} for a resume.`
  : 'There is no price listed for this model.')
const priceTiles = (pricing) => [
  { label: 'Input', value: formatPrice(pricing.input), hint: 'What OpenAI charges for what is sent to the model: the prompt, your resume and the job.' },
  { label: 'Cached input', value: formatPrice(pricing.cached_input), hint: 'The start of a prompt OpenAI has seen a moment ago is charged at this lower price.' },
  { label: 'Output', value: formatPrice(pricing.output), hint: 'What OpenAI charges for what the model writes, including the thinking it does first.' },
]
const number = (value) => value.toLocaleString('en-US')
const showCompare = ref(false)

// One line about each model, by id.
const NOTES = {
  'gpt-5': 'Most capable',
  'gpt-5-mini': 'Fast and affordable',
  'gpt-5-nano': 'Fastest and cheapest',
  'gpt-4.1': 'Precise and reliable',
  'gpt-4o-mini': 'Light and quick',
}

// Stars twinkling in the hero: a fixed set, spread out, each on its own rhythm.
const STARS = Array.from({ length: 14 }, (_, id) => ({
  id,
  style: { left: `${(id * 37) % 96 + 2}%`, top: `${(id * 53) % 70 + 6}%`, animationDelay: `${(id % 7) * 0.45}s`, animationDuration: `${2.4 + (id % 5) * 0.5}s`, '--s': `${0.6 + (id % 4) * 0.25}` },
}))

// The two sections under the AI switch.
const TABS = [{ id: 'models', label: 'AI Models' }, { id: 'prompt', label: 'Resume Prompt' }]
const MAX_PROMPT = 20000

const store = useJobSearchStore()
const openai = useOpenAiStore()
const ai = useJobAiStore()
const saving = ref(false)
const error = ref(null)
const tab = ref('models')
const enabled = computed(() => store.settings.ai_enabled)

// The prompt being edited. It follows what is saved until the user types, so a prompt saved elsewhere is not lost, and what is typed is
// not thrown away when something else on the page saves.
const draft = ref(store.settings.ai_resume_prompt || '')
const dirty = computed(() => draft.value.trim() !== (store.settings.ai_resume_prompt || '').trim())
const savedNote = ref('')
watch(() => store.settings.ai_resume_prompt, (saved, before) => { if (draft.value === (before || '')) draft.value = saved || '' })
// The settings load after the section opens (the first time), and then the draft starts from them.
watch(() => store.fetchedAt, () => { if (!dirty.value || !draft.value) draft.value = store.settings.ai_resume_prompt || '' })

// AI runs on the user's own OpenAI account, so it needs the OpenAI integration with a key OpenAI accepts. That is asked each time
// the section is opened (the user may just have connected it), and only then are the models loaded.
const checking = computed(() => !openai.loaded && openai.loading)
const ready = computed(() => openai.connection.connected)

onMounted(async () => {
  await openai.fetch()
  if (openai.connection.connected) ai.fetchModels()
})

// The saved model first when the list does not have it (still loading, or chosen before the list was cut down).
const modelOptions = computed(() => {
  const chosen = store.settings.ai_model
  return ai.models.some((model) => model.id === chosen) ? ai.models : [{ id: chosen, label: chosen, default: false }, ...ai.models]
})

async function save(patch) {
  saving.value = true
  error.value = null
  const result = await store.updateSettings(patch)
  saving.value = false
  if (!result.ok) error.value = result.message
}

async function savePrompt() {
  savedNote.value = ''
  await save({ ai_resume_prompt: draft.value })
  if (!error.value) {
    draft.value = store.settings.ai_resume_prompt
    savedNote.value = 'Saved'
    setTimeout(() => { savedNote.value = '' }, 2500)
  }
}

async function resetPrompt() {
  savedNote.value = ''
  await save({ ai_resume_prompt: '' })
  if (!error.value) draft.value = store.settings.ai_resume_prompt
}

const toggle = () => save({ ai_enabled: !store.settings.ai_enabled })
// A model can only be picked while AI is on (the cards are disabled otherwise; this is the same rule for anything that gets past them).
// Every click counts. The card is chosen on screen at once, and the saves go one after another: a click that comes while an earlier one is
// still being saved is not ignored, it is saved next, so the card shown is always the last one clicked.
const cards = ref(null)
let savingModel = false
let nextModel = null

async function choose(id) {
  if (!store.settings.ai_enabled) return
  if (id === store.settings.ai_model && !savingModel) return

  store.settings = { ...store.settings, ai_model: id } // chosen on screen at once
  nextModel = id
  if (savingModel) return

  savingModel = true
  error.value = null
  while (nextModel) {
    const wanted = nextModel
    nextModel = null
    const result = await store.updateSettings({ ai_model: wanted })
    if (!result.ok) {
      error.value = result.message
      nextModel = null
      await store.fetchSettings({ silent: true }) // show what is really saved
      break
    }
    // The answer carries the model of the click it answers; a click made since is still the one to show.
    if (nextModel) store.settings = { ...store.settings, ai_model: nextModel }
  }
  savingModel = false
}

// Arrow keys move through the models like through any group of radio buttons, choosing as they go.
function onModelKey(event, index) {
  const last = modelOptions.value.length - 1
  const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key]
  if (!step && event.key !== 'Home' && event.key !== 'End') return

  event.preventDefault()
  const target = event.key === 'Home' ? 0 : event.key === 'End' ? last : Math.min(last, Math.max(0, index + step))
  choose(modelOptions.value[target].id)
  cards.value?.querySelectorAll('[role="radio"]')[target]?.focus()
}

// The light that follows the pointer over a card.
function spotlight(event) {
  const box = event.currentTarget.getBoundingClientRect()
  event.currentTarget.style.setProperty('--mx', `${event.clientX - box.left}px`)
  event.currentTarget.style.setProperty('--my', `${event.clientY - box.top}px`)
}
</script>

<style scoped>
@property --ai-angle { syntax: '<angle>'; inherits: false; initial-value: 0deg; }

/* The hero: a dark night sky with three coloured lights drifting through it. */
.ai-hero { background: radial-gradient(120% 140% at 0% 0%, #1e1b4b 0%, #0f172a 55%, #020617 100%); }
.ai-blob { position: absolute; border-radius: 9999px; filter: blur(46px); opacity: 0.55; will-change: transform; }
.ai-blob-a { width: 180px; height: 180px; left: -40px; top: -60px; background: #6366f1; animation: ai-drift-a 16s ease-in-out infinite; }
.ai-blob-b { width: 170px; height: 170px; right: -30px; top: -40px; background: #d946ef; animation: ai-drift-b 19s ease-in-out infinite; }
.ai-blob-c { width: 150px; height: 150px; right: 28%; bottom: -80px; background: #22d3ee; animation: ai-drift-c 22s ease-in-out infinite; }
@keyframes ai-drift-a { 0%, 100% { transform: translate(0, 0) scale(1); } 50% { transform: translate(70px, 40px) scale(1.25); } }
@keyframes ai-drift-b { 0%, 100% { transform: translate(0, 0) scale(1); } 50% { transform: translate(-80px, 50px) scale(0.85); } }
@keyframes ai-drift-c { 0%, 100% { transform: translate(0, 0) scale(1); } 50% { transform: translate(-60px, -30px) scale(1.3); } }

/* Stars: tiny four point sparkles that fade in and out. */
.ai-star { position: absolute; width: 8px; height: 8px; opacity: 0; background: #fff; clip-path: polygon(50% 0, 62% 38%, 100% 50%, 62% 62%, 50% 100%, 38% 62%, 0 50%, 38% 38%); animation: ai-twinkle 3s ease-in-out infinite; }
@keyframes ai-twinkle { 0%, 100% { opacity: 0; transform: scale(0.3) rotate(0deg); } 50% { opacity: 0.9; transform: scale(var(--s, 1)) rotate(45deg); } }

/* The orb: a glowing core with a ring of light turning around it. */
.ai-orb { position: relative; width: 46px; height: 46px; }
.ai-orb-ring { position: absolute; inset: -3px; border-radius: 9999px; background: conic-gradient(from var(--ai-angle), #6366f1, #22d3ee, #d946ef, #6366f1); animation: ai-turn 4s linear infinite; filter: blur(0.5px); }
.ai-orb-core { position: absolute; inset: 0; border-radius: 9999px; display: inline-flex; align-items: center; justify-content: center; color: #fff; background: radial-gradient(circle at 30% 25%, #6366f1, #1e1b4b 70%); box-shadow: inset 0 0 18px rgb(255 255 255 / 0.25), 0 0 28px rgb(99 102 241 / 0.8); animation: ai-breathe 3.2s ease-in-out infinite; }
@keyframes ai-turn { to { --ai-angle: 360deg; } }
@keyframes ai-breathe { 0%, 100% { transform: scale(1); box-shadow: inset 0 0 18px rgb(255 255 255 / 0.25), 0 0 22px rgb(99 102 241 / 0.7); } 50% { transform: scale(1.06); box-shadow: inset 0 0 22px rgb(255 255 255 / 0.35), 0 0 38px rgb(167 139 250 / 0.95); } }

/* The title shimmers, as light passing over glass. */
.ai-title { background: linear-gradient(100deg, #fff 20%, #a5b4fc 40%, #67e8f9 50%, #f0abfc 60%, #fff 80%); background-size: 250% 100%; -webkit-background-clip: text; background-clip: text; color: transparent; animation: ai-shimmer 5s linear infinite; }
@keyframes ai-shimmer { from { background-position: 100% 0; } to { background-position: -150% 0; } }

/* The ticker: each line slides up, stays, and slides away, one after another. */
.ai-ticker-line { position: absolute; left: 0; top: 0; opacity: 0; transform: translateY(100%); animation: ai-line 12s ease-in-out infinite; white-space: nowrap; }
@keyframes ai-line { 0% { opacity: 0; transform: translateY(100%); } 3%, 22% { opacity: 1; transform: translateY(0); } 25%, 100% { opacity: 0; transform: translateY(-100%); } }
.ai-live { animation: ai-ping 1.8s ease-in-out infinite; }
@keyframes ai-ping { 0%, 100% { box-shadow: 0 0 0 0 rgb(52 211 153 / 0.6); } 50% { box-shadow: 0 0 0 5px rgb(52 211 153 / 0); } }

/* The model cards. Pointing at one lifts it, lights it up under the pointer and sweeps a shine across it; the chosen one has a border of moving
   colour and a ring that spreads when it is picked; pressing one pushes it in. */
.ai-card { --ai-surface: #fff; cursor: pointer; }
.ai-card:disabled { cursor: not-allowed; }
:global(.dark) .ai-card { --ai-surface: #1c2128; }
.ai-card-face { transition: transform 250ms cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 250ms ease, border-color 250ms ease; }
.ai-card-face::before { content: ''; position: absolute; inset: 0; pointer-events: none; opacity: 0; transition: opacity 250ms ease; background: radial-gradient(140px circle at var(--mx, 50%) var(--my, 50%), rgb(139 92 246 / 0.2), rgb(59 130 246 / 0.08) 45%, transparent 70%); }
.ai-card-face::after { content: ''; position: absolute; inset: 0; pointer-events: none; transform: translateX(-120%) skewX(-18deg); background: linear-gradient(90deg, transparent, rgb(255 255 255 / 0.5), transparent); }
.ai-card:not(:disabled):hover .ai-card-face { transform: translateY(-3px); border-color: rgb(167 139 250); box-shadow: 0 12px 24px -10px rgb(124 58 237 / 0.45), 0 0 0 1px rgb(167 139 250 / 0.35); }
.ai-card:not(:disabled):hover .ai-card-face::before { opacity: 1; }
.ai-card:not(:disabled):hover .ai-card-face::after { transform: translateX(120%) skewX(-18deg); transition: transform 700ms ease; }
.ai-card:not(:disabled):active .ai-card-face { transform: translateY(0) scale(0.97); transition-duration: 90ms; }
.ai-card:focus-visible .ai-card-face { outline: 2px solid #6366f1; outline-offset: 2px; }
.ai-card[aria-checked='true'] .ai-card-face { border-color: transparent; background: linear-gradient(var(--ai-surface), var(--ai-surface)) padding-box, conic-gradient(from var(--ai-angle), #6366f1, #22d3ee, #d946ef, #6366f1) border-box; animation: ai-turn 6s linear infinite; box-shadow: 0 10px 22px -12px rgb(99 102 241 / 0.55); }
.ai-card[aria-checked='true']::after { content: ''; position: absolute; inset: 0; border-radius: 0.75rem; pointer-events: none; border: 2px solid rgb(139 92 246 / 0.7); animation: ai-burst 600ms ease-out both; }
@keyframes ai-burst { from { transform: scale(1); opacity: 0.9; } to { transform: scale(1.14, 1.3); opacity: 0; } }

/* The button: a gradient that slowly moves, and an arrow that leans forward when you point at it. */
.ai-cta { background: linear-gradient(110deg, #4f46e5, #7c3aed, #d946ef, #4f46e5); background-size: 250% 100%; animation: ai-shimmer 6s linear infinite; box-shadow: 0 8px 24px -8px rgb(124 58 237 / 0.7); transition: transform 0.2s ease, box-shadow 0.2s ease; }
.ai-cta:hover { transform: translateY(-1px); box-shadow: 0 12px 28px -8px rgb(124 58 237 / 0.9); }
.ai-arrow { transition: transform 0.2s ease; }
.ai-cta:hover .ai-arrow { transform: translateX(3px); }

/* AI is on: the switch fills with colour that keeps moving, and the knob's sparkle spins. */
.ai-on { background: linear-gradient(120deg, #4f46e5, #7c3aed, #c026d3, #4f46e5); background-size: 250% 100%; animation: ai-shimmer 7s linear infinite; box-shadow: 0 6px 16px -6px rgb(124 58 237 / 0.8); }
.ai-spark { animation: ai-twirl 2.4s ease-in-out infinite; }
@keyframes ai-twirl { 0%, 100% { transform: rotate(0deg) scale(1); } 50% { transform: rotate(180deg) scale(1.2); } }

/* AI is off: the page under the tabs is covered by a frosted light blue layer. What is under it can still be made out, which says "this is
   here, and it does not work yet"; the reason is written on top. It fades in. */
.ai-locked { background: rgb(191 219 254 / 0.5); -webkit-backdrop-filter: blur(3px) saturate(1.15); backdrop-filter: blur(3px) saturate(1.15); animation: ai-lock-in 400ms ease-out both; }
:global(.dark) .ai-locked { background: rgb(30 58 138 / 0.5); }
@keyframes ai-lock-in { from { opacity: 0; } to { opacity: 1; } }

/* Things rise into place when the section opens. */
.ai-rise { animation: ai-rise 520ms cubic-bezier(0.2, 0.8, 0.2, 1) both; }
@keyframes ai-rise { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }

@media (prefers-reduced-motion: reduce) {
  .ai-blob, .ai-star, .ai-orb-ring, .ai-orb-core, .ai-title, .ai-ticker-line, .ai-live, .ai-cta, .ai-locked, .ai-on, .ai-spark, .ai-rise,
  .ai-card[aria-checked='true'] .ai-card-face, .ai-card[aria-checked='true']::after { animation: none; }
  .ai-card:not(:disabled):hover .ai-card-face { transform: none; }
  .ai-star { opacity: 0.6; }
  .ai-ticker-line:first-child { opacity: 1; transform: none; }
}
</style>
